/**
 * ForkWorkflowDialog (E1 ownership lanes, spec 2026-09-18 §4.4): the one
 * dialog behind **Take ownership** (Overview header, platform process/order
 * rows) and **New from type** (library card). Both post the same fork; the
 * `mode` selects the provenance stamp and the copy. Take ownership carries
 * the one-way warning — the copy stops receiving product updates to its
 * type. On success the dialog closes and navigates to the new row.
 */
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui'
import { useWorkflowFork, type WorkflowForkMode } from '@/hooks/useWorkflowFork'
import type { WorkflowSummary } from '@/hooks/useWorkflows'

export interface ForkWorkflowDialogProps {
  workflow: Pick<WorkflowSummary, 'id' | 'name'>
  mode: WorkflowForkMode
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ForkWorkflowDialog({ workflow, mode, open, onOpenChange }: ForkWorkflowDialogProps) {
  const [name, setName] = useState('')
  const navigate = useNavigate()
  const fork = useWorkflowFork()
  const takeOwnership = mode === 'take_ownership'

  function close() {
    fork.reset()
    onOpenChange(false)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (fork.isPending) return
    try {
      const created = await fork.mutateAsync({
        workflowId: workflow.id,
        mode,
        name: name.trim() || null,
      })
      onOpenChange(false)
      navigate(`/workflows/${created.id}`)
    } catch {
      // The mutation's `error` renders below; the dialog stays open.
    }
  }

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) close() }}>
      <ModalContent size="sm" showClose={false}>
        <form onSubmit={submit}>
          <ModalHeader>
            <ModalTitle>
              {takeOwnership
                ? `Take ownership of “${workflow.name}”`
                : `New workflow from “${workflow.name}”`}
            </ModalTitle>
            <ModalDescription>
              {takeOwnership
                ? 'This copies the workflow into one you own and can edit step by step. It is one-way: the copy stops receiving product updates to this type.'
                : 'This creates a new workflow of your own, pre-filled from this type. It will not receive product updates to the type.'}
            </ModalDescription>
          </ModalHeader>
          <ModalBody className="space-y-2">
            <label htmlFor="fork-workflow-name" className="block text-sm font-medium text-foreground">
              Name
            </label>
            <Input
              id="fork-workflow-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`${workflow.name} (copy)`}
              maxLength={255}
              autoFocus
            />
            {fork.error && (
              <p className="text-sm text-destructive" role="alert">
                {fork.error.message}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={close} disabled={fork.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={fork.isPending}>
              {takeOwnership ? 'Take ownership' : 'Create workflow'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
